import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, type Me } from "../lib/api.js";

/**
 * Couture d'authentification côté client. Au montage, on interroge `/api/me` : un 401 signifie
 * « anonyme » (afficher la connexion), sinon l'utilisateur est chargé. La session vit dans un cookie
 * httpOnly géré par le serveur ; aucun jeton n'est conservé en JavaScript.
 */

type Status = "loading" | "authenticated" | "anonymous";

interface AuthValue {
  status: Status;
  user: Me | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<Me | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((me) => {
        if (cancelled) return;
        setUser(me);
        setStatus("authenticated");
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        setStatus("anonymous");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { user: me } = await api.login(email, password);
    setUser(me);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (err) {
      // Une déconnexion qui échoue côté réseau ne doit pas bloquer le retour à l'écran de connexion.
      if (!(err instanceof ApiError)) throw err;
    }
    setUser(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthValue>(() => ({ status, user, login, logout }), [status, user, login, logout]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth doit être utilisé dans <AuthProvider>.");
  return ctx;
}
